/**
 * mirror-reports.ts — server → IDB mirror helper (Fase 2a).
 *
 * Mirrors server-returned reports into IndexedDB so the list stays visible
 * when the device goes offline. Guard rule: NEVER overwrite a local record
 * with estado `en_cola` or `borrador` — those represent unsynced local edits
 * that must not be discarded.
 *
 * Designed for any role: admin mirrors ALL reports the server returns;
 * technician mirrors only their own. Scoping is the caller's responsibility
 * — this helper is scope-agnostic.
 */

import { getReportByServerId, putReport } from "./reports-local.js";
import type { EstadoInforme, TipoInforme } from "@/features/reports/types.js";

/**
 * Minimal server-report shape needed for IDB mirroring.
 * Structurally compatible with `Informe` from @/features/reports —
 * callers can pass `Informe[]` directly via TypeScript structural typing.
 */
export interface ServerReportMirrorInput {
  id: string;
  numero?: string | null;
  tipo: TipoInforme;
  clienteId?: string | null;
  cliente?: string | null;
  autorNombre?: string | null;
  parentId?: string | null;
  estado: EstadoInforme;
  payload: Record<string, unknown>;
  createdAt?: string;
  updatedAt: string;
  /** Server URL for the generated PDF — stored so offline code knows it exists. */
  pdfUrl?: string | null;
}

/**
 * Mirror a batch of server reports into IDB.
 *
 * For each server record:
 *   - If a local record exists with `estado` `en_cola` or `borrador`, skip it
 *     (unsynced local edit is authoritative — overwriting drops pending work).
 *   - Otherwise, upsert into IDB, preserving the existing `localId` when
 *     a local record is already associated with this `serverId`.
 */
export async function mirrorServerReports(
  serverReports: ServerReportMirrorInput[],
): Promise<void> {
  for (const s of serverReports) {
    const local = await getReportByServerId(s.id);
    // Guard: unsynced local edit wins — do not overwrite pending or draft work.
    if (local && (local.estado === "en_cola" || local.estado === "borrador")) continue;
    await putReport({
      localId: local?.localId ?? s.id,
      serverId: s.id,
      numero: s.numero ?? undefined,
      tipo: s.tipo,
      clienteId: s.clienteId ?? undefined,
      cliente: s.cliente ?? undefined,
      autorNombre: s.autorNombre ?? undefined,
      parentLocalId: local?.parentLocalId,
      estado: s.estado,
      payload: s.payload,
      createdAt: s.createdAt,
      updatedAt: s.updatedAt,
      // Carry the server PDF URL so offline code can show/fetch it later.
      pdfUrl: s.pdfUrl ?? undefined,
    });
  }
}
